import { useState } from "react";
import VeyraLogo from "@/components/brand/VeyraLogo";
import PasswordInput from "@/components/common/PasswordInput";
import { useAuth } from "@/store/AuthContext";
import { t } from "@/i18n";

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-xs text-[#a3a3a3]">{hint}</span>}
  </label>
);

// Pantalla de activación protegida de Veyra. Sirve dos modos según el estado
// real del backend (nunca un registro público):
// - Si todavía no existe propietario: crea la cuenta (POST /api/auth/setup).
// - Si ya existe (por ejemplo, tras la activación de prueba de Fase 2): pide
//   el mismo `setup_token` secreto (OWNER_SETUP_TOKEN del .env del backend)
//   para RECLAMAR el acceso con un correo/contraseña nuevos, sin crear un
//   segundo propietario y sin tocar proyectos/archivos ya guardados
//   (POST /api/auth/owner/reclaim actualiza el mismo documento `_id="owner"`).
const SetupPage = () => {
  const { setup, reclaimOwner, setupRequired } = useAuth();
  const reclaiming = setupRequired === false;
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", setupToken: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.password !== form.confirm) { setError(t.auth.passwordMismatch); return; }
    if (form.password.length < 8) { setError(t.auth.passwordHint); return; }
    setBusy(true);
    try {
      const action = reclaiming ? reclaimOwner : setup;
      await action({ name: form.name.trim(), email: form.email.trim(), password: form.password, setup_token: form.setupToken.trim() });
    } catch (err) {
      setError(t.auth[err.message] || t.auth.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="setup-page" className="flex min-h-screen items-center justify-center bg-[#101010] px-4 text-[#f4f1ec]">
      <div className="w-full max-w-md v-fade-up">
        <VeyraLogo size={30} className="mb-8" />
        <h1 className="text-2xl font-semibold tracking-tight">{reclaiming ? t.auth.reclaimTitle : t.auth.setupTitle}</h1>
        <p className="mt-2 text-sm leading-relaxed text-[#a3a3a3]">{reclaiming ? t.auth.reclaimSubtitle : t.auth.setupSubtitle}</p>
        <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
          <Field label={t.auth.name}>
            <input data-testid="setup-name" required className="v-input" placeholder={t.auth.namePh} value={form.name} onChange={set("name")} />
          </Field>
          <Field label={t.auth.email}>
            <input data-testid="setup-email" required type="email" className="v-input" placeholder={t.auth.emailPh} value={form.email} onChange={set("email")} />
          </Field>
          <Field label={t.auth.password} hint={t.auth.passwordHint}>
            <PasswordInput data-testid="setup-password" required minLength={8} value={form.password} onChange={set("password")} />
          </Field>
          <Field label={t.auth.confirmPassword}>
            <PasswordInput data-testid="setup-confirm-password" required value={form.confirm} onChange={set("confirm")} />
          </Field>
          <Field label={t.auth.setupToken} hint={t.auth.setupTokenHint}>
            <input data-testid="setup-token" required className="v-input" value={form.setupToken} onChange={set("setupToken")} />
          </Field>
          {error && <p data-testid="setup-error" className="text-sm text-[#e5655a]">{error}</p>}
          <button data-testid="setup-submit" type="submit" disabled={busy} className="v-btn-primary justify-center">{busy ? (reclaiming ? t.auth.reclaiming : t.auth.creating) : (reclaiming ? t.auth.reclaimButton : t.auth.createAccount)}</button>
        </form>
      </div>
    </div>
  );
};

export default SetupPage;
